// PageBlock.jsx
import React, { useState } from "react";
import parse from "html-react-parser";
import axios from "axios";
import { BASEURL } from "../constants";
import { 
  FileText, 
  ExternalLink, 
  Download, 
  Loader2, 
  Type,
  AlignLeft,
  Link as LinkIcon
} from "lucide-react";

const PageBlock = ({ blockContent, index }) => {
  const [loading, setLoading] = useState(false);

  const getSingleDoc = async (documentId) => {
    setLoading(true);
    try {
      const res = await axios.get(BASEURL + "Document/SingleDoc/" + documentId);
      if (res.data?.length === 0) {
        return alert("No document found");
      }
      window.open(res.data[0].downloadLink, "_blank");
    } catch (error) {
      alert("Unable to get the document");
    } finally {
      setLoading(false);
    }
  };

  const renderPageBlock = (blockContent) => {
    switch (parseInt(blockContent.typeOfBlock)) {
      // Section Title
      case 1:
        return (
          <div className="flex items-center gap-3 py-4 border-b border-gray-200">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Type className="w-4 h-4 text-blue-600" />
            </div>
            <h4 className="text-xl sm:text-2xl font-semibold text-gray-900">
              {blockContent.blockSubTitle}
            </h4>
          </div>
        );

      // Content Block with HTML
      case 2:
        return (
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200 p-5 sm:p-6 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <AlignLeft className="w-4 h-4 text-purple-600" />
              </div>
              <div className="flex-1">
                <div className="text-lg font-semibold text-gray-900 prose prose-sm max-w-none">
                  {parse(blockContent.blockDescription)}
                </div>
              </div>
            </div>
            <div className="ml-11 text-gray-600 leading-relaxed prose prose-sm max-w-none">
              {parse(blockContent.contentTextOfHtml)}
            </div>
          </div>
        );

      // Document/Link Block
      case 4:
        return (
          <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6 hover:shadow-md hover:border-blue-200 transition-all duration-200">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                {parseInt(blockContent.documentId) > 0 ? (
                  <Download className="w-5 h-5 text-blue-600" />
                ) : (
                  <LinkIcon className="w-5 h-5 text-blue-600" />
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                {blockContent.blockSubTitle && (
                  <h4 className="text-lg font-semibold text-gray-900 mb-1">
                    {blockContent.blockSubTitle}
                  </h4>
                )}
                
                {blockContent.blockDescription && (
                  <p className="text-gray-500 text-sm mb-3">
                    {blockContent.blockDescription}
                  </p>
                )}

                {parseInt(blockContent.documentId) > 0 ? (
                  <button
                    onClick={() => getSingleDoc(blockContent.documentId)}
                    disabled={loading}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg font-medium text-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Loading...</span>
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4" />
                        <span>{blockContent.blockName || "Download Document"}</span>
                      </>
                    )}
                  </button>
                ) : (
                  <a
                    href={blockContent.externalLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg font-medium text-sm transition-colors duration-200"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>{blockContent.blockName || "Open Link"}</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        );

      // Text Content Block
      case 5:
        return (
          <div className="bg-gradient-to-br from-amber-50 to-white rounded-xl border border-amber-200 p-5 sm:p-6">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText className="w-4 h-4 text-amber-600" />
              </div>
              <div className="flex-1">
                {blockContent.blockSubTitle && (
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    {blockContent.blockSubTitle}
                  </h4>
                )}
                <div className="text-gray-600 leading-relaxed prose prose-sm max-w-none">
                  {parse(blockContent.blockDescription)}
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const content = renderPageBlock(blockContent);
  
  if (!content) return null;

  return (
    <div 
      className="animate-fadeIn"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      {content}
    </div>
  );
};

export default PageBlock;